import React, {
  ReactNode, useRef, useState, useMemo, useCallback, forwardRef, ComponentType, RefObject, ForwardedRef, MouseEvent, FocusEvent, MutableRefObject, KeyboardEvent, useEffect,
} from 'react';

import { Booleanish } from 'ia-react-core/~/interfaces/Booleanish';
import useScrollContainer from 'ia-react-core/~/hooks/_useScrollContainer/useScrollContainer';
import DataFormat from '../../format/DataFormat';
import resolveKey from '../../../utils/components/_resolveKey';
import useDropListSelection from './hooks/useDropListSelection';
import useDropListEmptyValue from './hooks/useDropListEmptyValue';
import useDropListIds from './hooks/useDropListIds';
import useDropListValue from './hooks/useDropListValue';
import useDropListPopper from './hooks/useDropListPopper';
import useDropListClickOutside from './hooks/useDropListClickOutside';
import useDropListKeyboardNavigation from './hooks/useDropListKeyboardNavigation';
import useDropListEvents from './hooks/useDropListEvents';
import useDropListPlacement from './hooks/useDropListPlacement';
import useDropListItemScroll from './hooks/useDropListItemScroll';

import DropListItem from './DropListItem';
import {
  StyledDropListWrapper,
  StyledDropListContainer,
  StyledDropListScrollWrapper,
} from './DropList.styles';

export type CustomContentProps = {
  targetRef: RefObject<HTMLElement>;
  keepOpen: (event: MouseEvent<any> | FocusEvent<any>, options?: { focus?: boolean }) => void;
  triggerBlur: (event?: FocusEvent) => void;
};

export type CustomContent = (
  ReactNode |
  ((options: CustomContentProps) => ReactNode) |
  CustomContent[]
);

export interface DropListOption<OptionValue> {
  label?: ReactNode;
  value?: OptionValue;
  key?: string | string[];
  icon?: ReactNode;
  isSearch?: boolean;
  textLabel?: string;
  onClick?: (value: OptionValue, event: MouseEvent<any> | KeyboardEvent<any>, options?: { targetRef: MutableRefObject<HTMLElement> }) => void;
}

export interface DropListProps<Value = string, OptionValue = Value> {
  /** The dropdown value. */
  value?: Value;
  /** Disables the dropdown. */
  disabled?: boolean;
  /** Displays the dropdowned as read only. */
  readOnly?: boolean;
  /** Displays the dropdowned value only. */
  textOnly?: boolean;
  busy?: boolean;
  /** Array of options. */
  options?: DropListOption<OptionValue>[];
  /** Custom label for the message when the dropdown is empty. */
  emptyLabel?: string;
  /** Value to use when the dropdown is empty. */
  emptyValue?: string;
  /** Value to use when the dropdown is empty. */
  noEmptyValue?: boolean;
  /** Target element  */
  Target: ComponentType<any>;
  /** Role of the target */
  targetRole?: string;
  /** @ignore */
  offsetFromEdge?: number;
  /** @ignore */
  selectionDelay?: number;
  /** @ignore */
  required?: boolean;
  /** @ignore */
  isValid?: boolean;
  /** @ignore */
  hasWarning?: boolean;
  /** @ignore */
  hasCaution?: boolean;
  /** @ignore */
  isTouched?: boolean;
  /** @ignore */
  selectionByText?: boolean;
  /** @ignore */
  homeEndNavigation?: boolean;
  /** @ignore */
  selectFirstOption?: boolean;
  /** @ignore */
  customContent?: CustomContent;
  /** @ignore */
  isUnionControlGroup?: boolean;
  /** @ignore */
  targetProps?: any;
  ref?: ForwardedRef<any>;
  /** Callback when the value changes. */
  onValueChange?: (value: OptionValue | Value, options?: { type?: 'click' | 'change'; selectedOption?: DropListOption<OptionValue> }) => void;
  onTouched?: (value: Value) => void;
  onOptionFocused?: (value: OptionValue) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  onBlur?: (event: FocusEvent<HTMLDivElement>) => void;
  onOpened?: (isOpened: boolean) => void;
  /** @ignore */
  shouldStayOpened?: (
    value: Value | OptionValue,
    event?: MouseEvent | KeyboardEvent | { type: 'click' | 'keydown' },
    options?: { isOpened?: boolean; focusedActionIndex?: number }
  ) => boolean;
}

interface DropListInternalProps {
  dropListId: number;
}

export interface DropListComposedProps<Value, OptionValue = Value> extends DropListProps<Value, OptionValue>, DropListInternalProps {
}

const DropListDefaultProps: Partial<DropListComposedProps<unknown>> = {
  noEmptyValue: false,
  options: [],
  offsetFromEdge: 8,
  selectionDelay: 200,
  disabled: false,
  readOnly: false,
  textOnly: false,
  emptyValue: '',
  selectionByText: true,
  homeEndNavigation: true,
  selectFirstOption: false,
  emptyLabel: 'emptyLabel',
  shouldStayOpened: () => false,
};

const DropList = Object.assign(
  forwardRef(<Value, TargetProps = {}, OptionValue = Value>({
    dropListId, options, value, disabled, readOnly, textOnly, offsetFromEdge, selectionDelay, selectionByText, required, emptyLabel, emptyValue, noEmptyValue, isValid, hasWarning, hasCaution, isTouched, Target, homeEndNavigation, selectFirstOption, customContent, targetProps, targetRole, shouldStayOpened, busy, onValueChange, onTouched, onKeyDown, onBlur, onOpened, onOptionFocused, isUnionControlGroup, ...props
  }: DropListComposedProps<Value, OptionValue> & TargetProps, ref: ForwardedRef<HTMLDivElement>) => {
    const [isOpened, setIsOpenedState] = useState(false);

    const resolvedIsOpened = useMemo(() => Boolean(isOpened && (options.length || customContent)), [isOpened, options, customContent]);

    const setIsOpened = useCallback((newIsOpened: boolean) => {
      if (isOpened !== newIsOpened) {
        setIsOpenedState(newIsOpened);
        if (onOpened) onOpened(newIsOpened);
      }
    }, [isOpened, onOpened]);

    const {
      optionsWithEmpty,
      resolvedEmptyLabel,
    } = useDropListEmptyValue({ options, emptyValue, noEmptyValue, emptyLabel, required });

    const {
      internalValue,
      handleValueChange,
      selectedOptions,
      selectedOptionIndexes,
      lastClosedValue,
    } = useDropListValue<Value, OptionValue>({ options: optionsWithEmpty, value, emptyValue, isOpened, selectFirstOption, onValueChange });

    const [focusedActionIndex, setFocusedActionIndex] = useState(selectedOptionIndexes[0] || 0);

    useEffect(() => {
      if (isOpened) {
        setFocusedActionIndex(selectedOptionIndexes[0] || 0);
      }
    }, [isOpened, selectedOptionIndexes]);

    const isEmpty = useMemo(() => (
      (internalValue === emptyValue) ||
      !selectedOptions.length
    ), [internalValue, emptyValue, selectedOptions]);

    const { dropDownContainerId, selectedItemIds } = useDropListIds<OptionValue>({ dropListId, selectedOptions });

    const customTargetRef = useRef<HTMLDivElement>(null);
    const popperScrollWrapper = useRef<HTMLUListElement>(null);
    const popperRef = useRef<HTMLDivElement>(null);

    const targetRef = useMemo<RefObject<HTMLDivElement>>(() => ref as RefObject<HTMLDivElement> || customTargetRef, [ref]);

    const scrollContainer = useScrollContainer(targetRef);

    const { maxPopperHeight, placement } = useDropListPlacement({ scrollContainer, popperRef, isOpened, targetRef, offsetFromEdge });
    const { styles, attributes, state } = useDropListPopper({ targetRef, popperRef, placement, isOpened });
    const {
      keepOpen,
      handleTargetClick,
      handleItemClick,
      handleTargetBlur,
      handleDropListScroll,
      isBlurring,
    } = useDropListEvents<Value, OptionValue>({ internalValue, isOpened, setIsOpened, handleValueChange, targetRef, shouldStayOpened, onTouched, onBlur });

    useDropListClickOutside({ targetRef, popperRef, isOpened, setIsOpened, keepOpen });

    const {
      inputText,
      setInputText,
    } = useDropListSelection({
      options: optionsWithEmpty,
      focusedActionIndex,
      selectionDelay,
      selectionByText,
      setFocusedActionIndex,
      onOptionFocused,
      handleValueChange,
    });

    const { handleKeyboardNavigation } = useDropListKeyboardNavigation<Value, OptionValue>({
      options: optionsWithEmpty,
      focusedActionIndex,
      setFocusedActionIndex,
      handleValueChange,
      setIsOpened,
      isOpened,
      setInputText,
      inputText,
      lastClosedValue,
      homeEndNavigation,
      internalValue,
      shouldStayOpened,
      onKeyDown,
      onOptionFocused,
    });

    const resolveContent = useCallback((content: CustomContent) => {
      if (typeof content === 'function') return content({ keepOpen, targetRef, triggerBlur: handleTargetBlur });
      return content as ReactNode;
    }, [handleTargetBlur, keepOpen, targetRef]);

    const resolvedCustomContent = useMemo(() => {
      if (Array.isArray(customContent)) return customContent.map(resolveContent);
      return resolveContent(customContent);
    }, [customContent, resolveContent]);

    useDropListItemScroll({ popperScrollWrapper, isOpened, selectedItemIds });

    const activeDescendantProps = useMemo(() => (
      selectedItemIds.length ? {
        'aria-activedescendant': selectedItemIds.join(' '),
        tabIndex: -1,
      } : {}
    ), [selectedItemIds]);

    if (textOnly) {
      return (
        <>
          {selectedOptions.map((selectedOption, index) => {
            const selectValue = selectedOption?.value;
            const selectedLabel = (selectValue || selectValue as unknown as number === 0) ? selectedOption?.label : '';
            return <DataFormat key={index}>{selectedLabel}</DataFormat>;
          })}
        </>
      );
    }

    return (
      <StyledDropListWrapper isUnionControlGroup={isUnionControlGroup}>
        <Target
          role={targetRole}
          aria-controls={dropDownContainerId}
          aria-haspopup="listbox"
          aria-expanded={String(resolvedIsOpened)}
          options={options}
          {...props}
          targetProps={targetProps}
          internalValue={internalValue}
          value={value}
          disabled={disabled || readOnly}
          readOnly={readOnly}
          isValid={isValid}
          hasWarning={hasWarning}
          hasCaution={hasCaution}
          isTouched={isTouched}
          ref={targetRef}
          isOpened={resolvedIsOpened}
          isEmpty={isEmpty}
          placement={state?.placement}
          selectedOptions={selectedOptions}
          resolvedEmptyLabel={resolvedEmptyLabel}
          setIsOpened={setIsOpened}
          setValue={handleValueChange}
          onMouseDown={handleTargetClick}
          onKeyDown={handleKeyboardNavigation}
          onBlur={handleTargetBlur}
        />
        <StyledDropListContainer
          ref={popperRef}
          style={styles.popper}
          {...attributes.popper}
          isOpened={resolvedIsOpened}
          isBlurring={isBlurring}
          placement={state?.placement}
          maxPopperHeight={maxPopperHeight}
          busy={busy}
          onScroll={handleDropListScroll}
        >
          <StyledDropListScrollWrapper
            id={dropDownContainerId}
            role="listbox"
            {...activeDescendantProps}
            aria-disabled={String(disabled) as Booleanish}
            aria-readonly={String(readOnly)as Booleanish}
            aria-hidden={String(!resolvedIsOpened) as Booleanish}
            ref={popperScrollWrapper}
          >
            {optionsWithEmpty.map(({ icon, label, value: optionValue = emptyValue as OptionValue, isSearch, key, onClick }, index) => {
              const resolvedKey = resolveKey(optionValue as unknown as string, key);
              return (
                <DropListItem<OptionValue>
                  id={`DropList${dropListId}Item${resolvedKey}`}
                  key={resolvedKey}
                  role="option"
                  icon={icon}
                  label={label}
                  value={optionValue}
                  isFocused={focusedActionIndex === index}
                  isEmpty={optionValue === emptyValue}
                  aria-selected={String(selectedOptionIndexes.includes(index)) as Booleanish}
                  isSearch={isSearch}
                  targetRef={targetRef}
                  onItemClick={onClick || handleItemClick}
                />
              );
            })}
          </StyledDropListScrollWrapper>
          {resolvedCustomContent}
        </StyledDropListContainer>
      </StyledDropListWrapper>
    );
  }),
  {
    defaultProps: DropListDefaultProps,
  },
);

export default DropList;
